/**
 * Surface Filters — Interactivity API store.
 *
 * Manages scenario-based filter state (Risikoklasse, Antall etasjer, Brukskategori).
 * All filtering, badge computation, and sorting happens client-side — no navigation needed.
 */

import { store, getContext } from "@wordpress/interactivity";

interface StructuralRow {
	virksomhet: string;
	antall_etasjer: string;
	risikoklasse: string;
	brannklasse: string;
	branncelle_under_200m2_kledning: string;
	branncelle_over_200m2_kledning: string;
	romningsvei_kledning: string;
	sjakter_hulrom_kledning: string;
}

interface SurfaceFiltersState {
	filterOptions: {
		risikoklasse: string[];
		antall_etasjer: string[];
	};
	structuralLookup: StructuralRow[];
	risikoklasseVirksomhetMap: Record<string, string>;
	requirementLabels: Record<string, string>;
	selectedRisikoklasse: string;
	selectedEtasjer: string;
	selectedKledning: string;

	/** Pre-computed status map: surfaceId → "risikoklasse|etasjer" → { kledningKey: status }. */
	surfaceStatuses: Record<string, Record<string, Record<string, string>>>;

	/** All surface IDs for counting. */
	surfaceIds: string[];

	/** View mode: 'card' or 'table'. */
	viewMode: string;

	// Derived.
	etasjerOptions: string[];
	hasActiveFilters: boolean;
	hasCompleteScenario: boolean;
	activeFilterLabels: Array<{ key: string; label: string }>;

	// Status counts.
	approvedCount: number;
	conditionalCount: number;
	showStatusCounts: boolean;
	approvedCountLabel: string;
	conditionalCountLabel: string;
	isCardView: boolean;
	isTableView: boolean;

	// Scenario summary derived.
	matchedScenarioRow: StructuralRow | null;
	hasScenarioMatch: boolean;
	scenarioBrannklasse: string;
	scenarioVirksomhet: string;
	scenarioRisikoklasseLabel: string;
	scenarioEtasjerLabel: string;
	scenarioRequirementLabel: string;
	scenarioRequirementValue: string;
}

const STATUS_ORDER: Record<string, number> = {
	approved: 0,
	conditional: 1,
	"not-applicable": 2,
};

/** Order value for surfaces with no data for the selected scenario. */
const UNKNOWN_ORDER = 3;

const STATUS_LABELS: Record<string, string> = {
	approved: "Kan benyttes",
	conditional: "Avklares med RIB",
	"not-applicable": "Ikke aktuelt",
};

const UNKNOWN_LABEL = "Mangler data";

/**
 * Wrap a state mutation in a View Transition for smooth reorder animation.
 */
function withViewTransition(fn: () => void): void {
	const doc = document as Document & {
		startViewTransition?: (cb: () => void) => void;
	};

	if (doc.startViewTransition) {
		doc.startViewTransition(fn);
	} else {
		fn();
	}
}

/**
 * Look up the status for a specific surface given the current scenario.
 * When the scenario has no requirements, every surface is approved.
 */
function getStatusForSurface(surfaceId: string): string | null {
	if (
		!state.selectedRisikoklasse ||
		!state.selectedEtasjer ||
		!state.selectedKledning
	) {
		return null;
	}

	// No requirement for this scenario → all surfaces can be used.
	if (state.scenarioHasNoRequirement) return "approved";

	const key = `${state.selectedRisikoklasse}|${state.selectedEtasjer}`;
	return (
		state.surfaceStatuses?.[surfaceId]?.[key]?.[state.selectedKledning] ?? null
	);
}

const { state } = store<{ state: SurfaceFiltersState }>(
	"byggma-brann-og-lyd/surface-filters",
	{
		state: {
			get etasjerOptions(): string[] {
				if (!state.selectedRisikoklasse) {
					return state.filterOptions?.antall_etasjer ?? [];
				}

				const matching = state.structuralLookup.filter(
					(row) => row.risikoklasse === state.selectedRisikoklasse,
				);

				return [
					...new Set(matching.map((r) => r.antall_etasjer).filter(Boolean)),
				];
			},

			get hasActiveFilters(): boolean {
				return (
					state.selectedRisikoklasse !== "" ||
					state.selectedEtasjer !== "" ||
					state.selectedKledning !== ""
				);
			},

			get hasCompleteScenario(): boolean {
				return (
					state.selectedRisikoklasse !== "" &&
					state.selectedEtasjer !== "" &&
					state.selectedKledning !== ""
				);
			},

			get approvedCount(): number {
				if (!state.hasCompleteScenario) return 0;
				return (state.surfaceIds || []).filter(
					(id) => getStatusForSurface(id) === "approved",
				).length;
			},

			get conditionalCount(): number {
				if (!state.hasCompleteScenario) return 0;
				return (state.surfaceIds || []).filter(
					(id) => getStatusForSurface(id) === "conditional",
				).length;
			},

			get showStatusCounts(): boolean {
				return state.hasCompleteScenario;
			},

			get approvedCountLabel(): string {
				return `${state.approvedCount} Kan benyttes`;
			},

			get conditionalCountLabel(): string {
				return `${state.conditionalCount} Avklares med RIB`;
			},

			get listingSummary(): string {
				const total = (state.surfaceIds || []).length;
				if (!state.hasCompleteScenario) {
					return `Viser totalt ${total} overflater`;
				}
				return `Viser totalt ${total} overflater, ${state.approvedCount} kan benyttes og ${state.conditionalCount} m\u00e5 avklares med RIB`;
			},

			get isCardView(): boolean {
				return (state.viewMode || "card") === "card";
			},

			get isTableView(): boolean {
				return state.viewMode === "table";
			},

			get activeFilterLabels(): Array<{ key: string; label: string }> {
				const labels: Array<{ key: string; label: string }> = [];

				if (state.selectedRisikoklasse) {
					labels.push({
						key: "risikoklasse",
						label: `Risikoklasse: ${state.selectedRisikoklasse}`,
					});
				}

				if (state.selectedEtasjer) {
					labels.push({
						key: "etasjer",
						label: `Antall etasjer: ${state.selectedEtasjer}`,
					});
				}

				if (state.selectedKledning) {
					labels.push({
						key: "kledning",
						label:
							state.requirementLabels[state.selectedKledning] ||
							state.selectedKledning,
					});
				}

				return labels;
			},

			/**
			 * Find the structural lookup row matching the selected risikoklasse + etasjer.
			 */
			get matchedScenarioRow(): StructuralRow | null {
				if (!state.selectedRisikoklasse || !state.selectedEtasjer) return null;

				return (
					state.structuralLookup.find(
						(row) =>
							row.risikoklasse === state.selectedRisikoklasse &&
							row.antall_etasjer === state.selectedEtasjer,
					) ?? null
				);
			},

			get hasScenarioMatch(): boolean {
				return state.matchedScenarioRow !== null;
			},

			get scenarioBrannklasse(): string {
				const val = state.matchedScenarioRow?.brannklasse || "";
				return val && val !== "-" ? val : "\u2014";
			},

			get scenarioVirksomhet(): string {
				if (!state.selectedRisikoklasse) return "\u2014";
				return (
					state.risikoklasseVirksomhetMap[state.selectedRisikoklasse] || "\u2014"
				);
			},

			get scenarioRisikoklasseLabel(): string {
				return state.selectedRisikoklasse || "\u2014";
			},

			get scenarioEtasjerLabel(): string {
				return state.selectedEtasjer || "\u2014";
			},

			get scenarioRequirementLabel(): string {
				if (!state.selectedKledning) return "";
				return (
					state.requirementLabels[state.selectedKledning] ||
					state.selectedKledning
				);
			},

			get scenarioRequirementValue(): string {
				const row = state.matchedScenarioRow;
				if (!row || !state.selectedKledning) return "";

				const key = state.selectedKledning as keyof StructuralRow;
				const value = row[key] || "";
				if (!value || value === "-") {
					return "Ingen brannkrav gjelder for dette scenariet";
				}
				return value;
			},

			get scenarioHasNoRequirement(): boolean {
				if (!state.hasCompleteScenario || !state.matchedScenarioRow) return false;

				const row = state.matchedScenarioRow;
				const key = state.selectedKledning as keyof StructuralRow;
				const value = (row[key] || "").trim();
				return !value || value === "-";
			},
		},

		actions: {
			setRisikoklasse(event: Event) {
				const target = event.target as HTMLInputElement;
				withViewTransition(() => {
					state.selectedRisikoklasse = target.value;
					// Clear etasjer only if the current value is no longer valid.
					if (
						state.selectedEtasjer &&
						!state.etasjerOptions.includes(state.selectedEtasjer)
					) {
						state.selectedEtasjer = "";
					}
				});
			},

			setEtasjer(event: Event) {
				const target = event.target as HTMLInputElement;
				withViewTransition(() => {
					state.selectedEtasjer = target.value;
				});
			},

			setKledning(event: Event) {
				const target = event.target as HTMLInputElement;
				withViewTransition(() => {
					state.selectedKledning = target.value;
				});
			},

			removeFilter() {
				const context = getContext<{ item: { key: string } }>();
				const filterKey = context.item.key;

				withViewTransition(() => {
					if (filterKey === "risikoklasse") {
						state.selectedRisikoklasse = "";
						// Clear etasjer only if no longer valid without a risikoklasse filter.
						if (
							state.selectedEtasjer &&
							!state.etasjerOptions.includes(state.selectedEtasjer)
						) {
							state.selectedEtasjer = "";
						}
					} else if (filterKey === "etasjer") {
						state.selectedEtasjer = "";
					} else if (filterKey === "kledning") {
						state.selectedKledning = "";
					}
				});
			},

			clearFilters() {
				withViewTransition(() => {
					state.selectedRisikoklasse = "";
					state.selectedEtasjer = "";
					state.selectedKledning = "";
				});
			},

			setViewMode(event: Event) {
				const target = event.currentTarget as HTMLButtonElement;
				const mode = target.dataset.viewMode || "card";
				withViewTransition(() => {
					state.viewMode = mode;
				});
			},
		},

		callbacks: {
			/**
			 * Get the CSS order value for a surface card (used by data-wp-style--order).
			 */
			getOrder(): string {
				const ctx = getContext<{ surfaceId: string }>();
				const status = getStatusForSurface(ctx.surfaceId);

				// No scenario selected → neutral order.
				if (!state.hasCompleteScenario) return "0";

				// Missing data → sort last.
				if (!status) return String(UNKNOWN_ORDER);

				return String(STATUS_ORDER[status] ?? UNKNOWN_ORDER);
			},

			/**
			 * Whether to show the status badge on this card.
			 */
			hasStatus(): boolean {
				if (!state.hasCompleteScenario) return false;

				// Show badge for all statuses and missing data.
				return true;
			},

			/**
			 * Get the status label text for this card's badge.
			 */
			getStatusLabel(): string {
				const ctx = getContext<{ surfaceId: string }>();
				const status = getStatusForSurface(ctx.surfaceId);

				if (!status) return state.hasCompleteScenario ? UNKNOWN_LABEL : "";

				return STATUS_LABELS[status] || "";
			},

			/**
			 * Whether this card's status is "approved".
			 */
			isApproved(): boolean {
				const ctx = getContext<{ surfaceId: string }>();
				return getStatusForSurface(ctx.surfaceId) === "approved";
			},

			/**
			 * Whether this card's status is "conditional".
			 */
			isConditional(): boolean {
				const ctx = getContext<{ surfaceId: string }>();
				return getStatusForSurface(ctx.surfaceId) === "conditional";
			},

			/**
			 * Whether this card's status is "not-applicable".
			 */
			isNotApplicable(): boolean {
				const ctx = getContext<{ surfaceId: string }>();
				return getStatusForSurface(ctx.surfaceId) === "not-applicable";
			},

			/**
			 * Whether this card has no data for the selected scenario.
			 */
			isUnknown(): boolean {
				if (!state.hasCompleteScenario) return false;

				const ctx = getContext<{ surfaceId: string }>();
				return getStatusForSurface(ctx.surfaceId) === null;
			},

			/**
			 * Whether the risikoklasse radio in this context is checked.
			 */
			isRisikoklasseChecked(): boolean {
				const ctx = getContext<{ radioValue?: string; item?: string }>();
				return state.selectedRisikoklasse === (ctx.radioValue ?? ctx.item ?? "");
			},

			/**
			 * Whether the etasjer radio in this context is checked.
			 */
			isEtasjerChecked(): boolean {
				const ctx = getContext<{ radioValue?: string; item?: string }>();
				return state.selectedEtasjer === (ctx.radioValue ?? ctx.item ?? "");
			},

			/**
			 * Whether the kledning radio in this context is checked.
			 */
			isKledningChecked(): boolean {
				const ctx = getContext<{ radioValue?: string; item?: string }>();
				return state.selectedKledning === (ctx.radioValue ?? ctx.item ?? "");
			},
		},
	},
);
